/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, DatePicker, message, Input, Skeleton } from "antd";

const { RangePicker } = DatePicker;
import { ArrowLeftOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, mapReportCategoryToFigureType, mapBusinessTypeToFigureSubtype, mapZoneNameToZoneId } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import axios from "axios";
import "./style.scss";
import "../style.scss";



const CPWiseViewCentralOps = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDateRange, setSelectedDateRange] = useState([moment().startOf("month"), moment()]);
    const [loading, setLoading] = useState(false);
    const [cpWiseData, setCpWiseData] = useState([]);
    const [zone, setZone] = useState("");
    const [hub, setHub] = useState("");
    const [hubId, setHubId] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [reportType, setReportType] = useState("Piece Rate");
    const [reportCategory, setReportCategory] = useState("Shipment Counts");
    const [dataType, setDataType] = useState("today"); // "today" or "range"
    const hasLoadedRef = useRef(false);
    const isInitializingRef = useRef(true);

    useEffect(() => {
        // Get zone, hub, report category, type, and data type from location state if available
        const zoneValue = location.state?.zone || "";
        const hubValue = location.state?.hub || "";
        const hubIdValue = location.state?.hubId || "";
        const category = location.state?.reportCategory || "Shipment Counts";
        const type = location.state?.reportType || "Piece Rate";
        const typeMode = location.state?.type || "today";
        const businessTypeValue = location.state?.businessType || "";

        isInitializingRef.current = true;

        setZone(zoneValue);
        setHub(hubValue);
        setHubId(hubIdValue);
        setReportCategory(category);
        setReportType(type);
        setDataType(typeMode);
        setBusinessType(businessTypeValue);

        if (typeMode === "today") {
            if (location.state?.date) {
                setSelectedDate(moment(location.state.date, "YYYY-MM-DD", true));
            }
        } else {
            // For range type
            if (location.state?.date && location.state?.endDate) {
                const startDate = moment(location.state.date, "YYYY-MM-DD", true);
                const endDate = moment(location.state.endDate, "YYYY-MM-DD", true);
                if (startDate.isValid() && endDate.isValid()) {
                    setSelectedDateRange([startDate, endDate]);
                }
            } else if (location.state?.date) {
                const dateValue = moment(location.state.date, "YYYY-MM-DD", true);
                if (dateValue.isValid()) {
                    setSelectedDateRange([dateValue, dateValue]);
                }
            }
        }

        // Allow state updates to complete
        setTimeout(() => {
            isInitializingRef.current = false;
        }, 0);
    }, [location.state]);

    const loadCPWiseData = useCallback(async (signal) => {
        if (!hub || !zone || !hubId || isInitializingRef.current) return;

        // Use default businessType if empty
        const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");

        setLoading(true);

        try {
            const figureType = mapReportCategoryToFigureType(reportCategory);
            const figureSubtype = mapBusinessTypeToFigureSubtype(finalBusinessType, reportCategory);
            const zoneId = mapZoneNameToZoneId(zone);

            if (!zoneId) {
                message.error("Invalid zone name");
                setCpWiseData([]);
                setLoading(false);
                return;
            }

            let fromDate, toDate;
            if (dataType === "today") {
                fromDate = selectedDate.format("YYYY-MM-DD");
                toDate = selectedDate.format("YYYY-MM-DD");
            } else {
                if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                    fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                    toDate = selectedDateRange[1].format("YYYY-MM-DD");
                } else {
                    fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                    toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                }
            }

            const response = await DirectorDashboardService3.getZoneHubCpData(
                figureType,
                figureSubtype,
                fromDate,
                toDate,
                zoneId,
                hubId,
                { signal }
            );

            if (response?.data?.cpWiseSummary) {
                // Format value based on report category
                const formatValue = (value) => {
                    const numValue = typeof value === "number" ? value : parseFloat(value) || 0;
                    return formatIndianNumber(numValue);
                };

                // Transform cpWiseSummary object to array format
                const cpSummary = response.data.cpWiseSummary;
                const transformedData = Object.entries(cpSummary)
                    .filter(([cpName, total]) => {
                        // Filter out Total and 0 values
                        if (cpName === "Total") return false;
                        const numValue = typeof total === "number" ? total : parseFloat(total) || 0;
                        return numValue > 0;
                    })
                    .map(([cpName, total]) => ({
                        key: cpName,
                        cp: cpName,
                        total: formatValue(total || 0),
                    }));

                // Add Total row at the end
                if (cpSummary.Total !== undefined) {
                    transformedData.push({
                        key: "total",
                        cp: "Total",
                        total: formatValue(cpSummary.Total || 0),
                    });
                }

                if (!signal?.aborted) setCpWiseData(transformedData);
            } else {
                if (!signal?.aborted) {
                    setCpWiseData([]);
                    message.warning("No data available for the selected date range");
                }
            }
        } catch (error) {
            if (axios.isCancel(error)) return;
            console.error("Error loading CP wise data:", error);
            message.error(error?.message || "Failed to load CP wise data");
            if (!signal?.aborted) setCpWiseData([]);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [reportCategory, businessType, hub, zone, hubId, dataType, selectedDate, selectedDateRange, location.state]);

    useEffect(() => {
        // Load data when component mounts or reportCategory/hub/zone/dataType changes
        // Wait for initialization to complete
        const controller = new AbortController();

        const checkAndLoad = () => {

            if (isInitializingRef.current) {
                setTimeout(checkAndLoad, 50);
                return;
            }

            // Ensure we have required data
            if (!hub || !zone || !hubId || !reportCategory) {
                return;
            }

            // Use default businessType if empty
            const finalBusinessType = businessType || (reportCategory === "Delivery Data" ? "Total DRS" : "Account");

            const dateKey = dataType === "today"
                ? selectedDate.format("YYYY-MM-DD")
                : `${selectedDateRange[0]?.format("YYYY-MM-DD")}-${selectedDateRange[1]?.format("YYYY-MM-DD")}`;
            const loadKey = `${reportCategory}-${finalBusinessType}-${zone}-${hub}-${hubId}-${dataType}-${dateKey}`;

            if (!hasLoadedRef.current || hasLoadedRef.current !== loadKey) {
                hasLoadedRef.current = loadKey;
                loadCPWiseData(controller.signal);
            }
        };

        checkAndLoad();

        return () => controller.abort();
    }, [reportCategory, businessType, zone, hub, hubId, dataType, selectedDate, selectedDateRange, loadCPWiseData]);


    const handleDateSubmit = () => {
        hasLoadedRef.current = null; // Reset to allow reload
        loadCPWiseData();
    };

    const getDisplayDate = () => {
        if (dataType === "today") {
            return selectedDate.format("DD/MM/YYYY");
        } else {
            if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                return `${selectedDateRange[0].format("DD/MM/YYYY")} - ${selectedDateRange[1].format("DD/MM/YYYY")}`;
            }
            return `${moment().startOf("month").format("DD/MM/YYYY")} - ${moment().format("DD/MM/YYYY")}`;
        }
    };
    const displayDate = getDisplayDate();

    const handleGoBack = () => {
        const routeMap = {

            "Shipment Counts": "/central-ops/hub-wise-shipment-counts",
            "Delivery Data": "/central-ops/hub-wise-delivery-data",
        };

        let dateToPass, endDateToPass;
        if (dataType === "today") {
            dateToPass = selectedDate.format("YYYY-MM-DD");
            endDateToPass = null;
        } else {
            if (selectedDateRange && selectedDateRange.length === 2) {
                dateToPass = selectedDateRange[0].format("YYYY-MM-DD");
                endDateToPass = selectedDateRange[1].format("YYYY-MM-DD");
            } else {
                dateToPass = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                endDateToPass = location.state?.endDate || moment().format("YYYY-MM-DD");
            }
        }

        navigate(routeMap[reportCategory] || "/central-ops/hub-wise-business-data", {
            state: {
                zone: zone,
                date: dateToPass,
                endDate: endDateToPass,
                reportType: reportType,
                reportCategory: reportCategory,
                businessType: businessType,
                type: dataType,
            },
        });
    };



    return (
        <div className="director-dashboard cp-wise-page">
            {/* Unified Header Bar */}
            <div className="cp-wise-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <span className="page-title">
                        CP Wise {reportCategory} - {displayDate} - {zone} - {hub}
                    </span>
                </div>
                <div className="header-right">
                    <Input
                        placeholder="Search by CP Name"
                        prefix={<SearchOutlined />}
                        className="search-input"
                    />
                    {dataType === "range" ? (
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={selectedDateRange}
                            onChange={(dates) => {
                                if (dates && dates.length === 2) {
                                    setSelectedDateRange([dates[0], dates[1]]);
                                } else {
                                    setSelectedDateRange([moment().startOf("month"), moment()]);
                                }
                            }}
                            className="cp-wise-date-picker"
                        />
                    ) : (
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={selectedDate}
                            onChange={(date) => setSelectedDate(date || moment())}
                            className="cp-wise-date-picker"
                        />
                    )}
                    <Button type="primary" onClick={handleDateSubmit} className="cp-wise-submit-btn">
                        Submit
                    </Button>
                </div>
            </div>

            {/* CP List Grid */}
            <div className="cp-wise-grid">
                {/* CP Cards */}
                {loading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                        <div
                            className="cp-wise-card"
                            key={`skeleton-${index}`}
                            style={{ cursor: "default" }}
                        >
                            <div className="card-header">
                                <Skeleton.Input active size="small" style={{ width: 120 }} />
                            </div>
                            <div className="card-value">
                                <Skeleton.Input active size="large" style={{ width: 100 }} />
                            </div>
                        </div>
                    ))
                ) : (
                    cpWiseData.map((item) => {
                        // Skip Total row in grid, it's shown in footer
                        if (item.cp === "Total") return null;

                        return (
                            <div
                                className="cp-wise-card"
                                key={item.key}
                                style={{ cursor: "default" }}
                            >
                                <div className="card-header">
                                    <span className="cp-name" title={item.cp}>{item.cp}</span>
                                    <InfoCircleOutlined className="info-icon" />
                                </div>
                                <div className="card-value">
                                    {item.total}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>


            {/* Total Footer */}
            {cpWiseData.length > 0 && (
                <div className="cp-wise-footer">
                    <div className="footer-left">
                        <span className="footer-label">Total CP Count : </span>
                        {/* Subtract 1 for the 'Total' row if it exists in data */}
                        <span className="footer-value">
                            {Math.max(0, cpWiseData.filter(i => i.cp !== "Total").length).toString().padStart(2, "0")}
                        </span>
                    </div>
                    <div className="footer-right">
                        <span className="footer-label">
                            Total Count :
                        </span>
                        <span className="footer-value">

                            {cpWiseData.find(i => i.cp === "Total")?.total || "0"}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CPWiseViewCentralOps;

